import type {
  BrandCategory,
  SecurityCatalogProduct,
  SecurityFormula,
  SecurityLevels,
  SecurityPropertyType,
  SecurityRequestClientResponse,
  SecurityRequestStatus,
  SecurityRequestType,
  SecurityStudyReason,
  SecuritySurfaceRange,
} from '@wisecure/api-client';

/** French labels and formatters shared by the security requests and catalogue pages. */

export const REQUEST_TYPE_LABELS: Record<SecurityRequestType, string> = {
  NEW_SYSTEM: 'Nouveau système',
  STUDY: 'Étude',
  COMPLEMENT: 'Complément',
  REPAIR: 'Dépannage',
};

export const REQUEST_TYPE_COLORS: Record<SecurityRequestType, string> = {
  NEW_SYSTEM: 'blue',
  STUDY: 'purple',
  COMPLEMENT: 'cyan',
  REPAIR: 'orange',
};

export const REQUEST_STATUS_LABELS: Record<SecurityRequestStatus, string> = {
  PENDING: 'Non traitée',
  HANDLED: 'Traitée',
};

export const CATEGORY_LABELS: Record<BrandCategory, string> = {
  ALARME_INTRUSION: 'Alarme',
  VIDEO_SURVEILLANCE: 'Vidéo',
  CONTROLE_D_ACCES: "Contrôle d'accès",
  DOMOTIQUE: 'Domotique',
  ALARME_INCENDIE: 'Incendie',
};

export const CATEGORY_COLORS: Record<BrandCategory, string> = {
  ALARME_INTRUSION: 'red',
  VIDEO_SURVEILLANCE: 'blue',
  CONTROLE_D_ACCES: 'green',
  DOMOTIQUE: 'purple',
  ALARME_INCENDIE: 'orange',
};

export const FORMULAS: SecurityFormula[] = ['ESSENTIEL', 'CONFORT', 'PREMIUM'];

export const FORMULA_LABELS: Record<SecurityFormula, string> = {
  ESSENTIEL: 'Essentiel',
  CONFORT: 'Confort',
  PREMIUM: 'Premium',
};

export const PRODUCT_LABELS: Record<SecurityCatalogProduct, string> = {
  ALARM: 'Alarme',
  VIDEO: 'Vidéo',
  OPTION: 'Options',
};

export const PROPERTY_TYPE_LABELS: Record<SecurityPropertyType, string> = {
  HOUSE: 'Maison',
  APARTMENT: 'Appartement',
  BUSINESS: 'Local professionnel',
  BUILDING: 'Immeuble / copropriété',
  ERP: 'ERP',
};

export const SURFACE_LABELS: Record<SecuritySurfaceRange, string> = {
  LT_50: 'Moins de 50 m²',
  R50_100: '50 à 100 m²',
  R100_300: '100 à 300 m²',
  R300_800: '300 à 800 m²',
  GT_800: 'Plus de 800 m²',
};

export const LEVELS_LABELS: Record<SecurityLevels, string> = {
  SINGLE: 'Plain-pied',
  MULTI: 'Avec étage(s)',
};

export const STUDY_REASON_LABELS: Record<SecurityStudyReason, string> = {
  ERP: 'Établissement recevant du public',
  BUILDING: 'Immeuble / copropriété',
  LARGE_SURFACE: 'Surface supérieure à 300 m²',
  FIRE_OR_ACCESS_BUSINESS: "Incendie ou contrôle d'accès d'un local professionnel",
  MORE_THAN_20_DETECTORS: 'Plus de 20 détecteurs',
  MORE_THAN_8_CAMERAS: 'Plus de 8 caméras',
};

export const toOptions = <K extends string>(labels: Record<K, string>) =>
  (Object.keys(labels) as K[]).map((value) => ({ value, label: labels[value] }));

export function formatEuro(amount?: number | null): string {
  if (amount == null) return '—';
  return `${amount.toLocaleString('fr-FR', { maximumFractionDigits: 2 })} €`;
}

export function formatDateTime(date?: string | null): string {
  if (!date) return '—';
  return new Date(date).toLocaleString('fr-FR', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

/** Install time billed in half days, e.g. 5 → "2,5 jours". */
export function formatHalfDays(count: number): string {
  if (count === 1) return '½ journée';
  const days = count / 2;
  return `${days.toLocaleString('fr-FR')} jour${days > 1 ? 's' : ''}`;
}

export function clientDisplayName(client: SecurityRequestClientResponse): string {
  return [client.firstName, client.lastName].filter(Boolean).join(' ') || client.email;
}
